/**
 * A tag-style cell editor for entering multiple string values.
 * Stores values as a string[] array.
 * Users can type values and press comma/semicolon/enter to add tags.
 * Optionally shows autocomplete suggestions from the company text field options API.
 *
 * When the "WA-9367-work-rule-usability" flag is enabled, this cell editor delegates
 * its UI and interaction to the reusable {@link StringTagInput} component; otherwise
 * the legacy inline implementation is used.
 */
import { colorMiscBlue70, IconX } from "@rhumbix/rmbx_design_system_web"
import { forwardRef, useImperativeHandle, useState, useRef, KeyboardEvent, useEffect } from "react"
import { ICellEditorParams } from "ag-grid-community"

import { getDataFromSearchv4 } from "../../../api/get"
import { getFlagEnabled } from "../../../getFlagValue"
import StringTagInput from "../../string-tag-input"
import type { tStringTagInputHandle } from "../../string-tag-input"
import {
    TagEditorContainer,
    TagsContainer,
    Tag,
    TagText,
    RemoveButton,
    TagInput,
    ErrorMessage,
} from "./tag-editor-styles"
import { useSuggestions, SuggestionsDropdown } from "./useSuggestions"

const WARNING_MESSAGE_DURATION = 3000

/**
 * Extended props for the StringTagCellEditor.
 * Includes optional suggestion source params passed via cellEditorParams.
 */
type tStringTagCellEditorParams = ICellEditorParams & {
    /** The model name for the companyTextFieldOptions search (e.g. "Employee", "Equipment") */
    suggestionModel?: string
    /** The field name for the companyTextFieldOptions search (e.g. "department") */
    suggestionFieldName?: string
}

/**
 * Parse the input value into a string array.
 * Handles both string[] and null/undefined inputs.
 */
const parseValues = (value: unknown): string[] => {
    if (!value) return []
    if (Array.isArray(value)) {
        return value.filter((item): item is string => typeof item === "string" && item.trim() !== "")
    }
    return []
}

/**
 * AG-Grid cell editor for entering multiple string values as tags.
 * Values are stored as a string[] array.
 * When suggestionModel and suggestionFieldName are provided, fetches distinct custom
 * field values from the API and shows them as autocomplete suggestions.
 */
const StringTagCellEditor = forwardRef((props: tStringTagCellEditorParams, ref) => {
    if (getFlagEnabled("WA-9367-work-rule-usability")) {
        return <StringTagCellEditorNew {...props} forwardedRef={ref} />
    }
    return <StringTagCellEditorLegacy {...props} forwardedRef={ref} />
})

export default StringTagCellEditor

type tInternalProps = tStringTagCellEditorParams & {
    forwardedRef: React.Ref<unknown>
}

/**
 * New implementation: delegates UI and interaction to the reusable StringTagInput
 * component. This editor is a thin AG-Grid adapter that manages the tag values
 * and exposes AG-Grid `getValue` / `afterGuiAttached` via ref.
 */
const StringTagCellEditorNew = (props: tInternalProps): JSX.Element => {
    const [values, setValues] = useState<string[]>(parseValues(props.value))
    const inputHandleRef = useRef<tStringTagInputHandle>(null)

    useImperativeHandle(props.forwardedRef, () => ({
        /** Return committed values plus any pending in-flight input text. */
        getValue: () => inputHandleRef.current?.getValueWithPending() ?? values,
        afterGuiAttached: () => {
            setTimeout(() => {
                inputHandleRef.current?.focus()
            }, 0)
        },
    }))

    return (
        <StringTagInput
            ref={inputHandleRef}
            values={values}
            onChange={setValues}
            suggestionModel={props.suggestionModel}
            suggestionFieldName={props.suggestionFieldName}
        />
    )
}

/**
 * Legacy inline implementation, preserved for use when the
 * "WA-9367-work-rule-usability" flag is disabled.
 */
const StringTagCellEditorLegacy = (props: tInternalProps): JSX.Element => {
    const [values, setValues] = useState<string[]>(parseValues(props.value))
    const [inputValue, setInputValue] = useState("")
    const [error, setError] = useState("")
    const inputRef = useRef<HTMLInputElement>(null)
    const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    const [allSuggestions, setAllSuggestions] = useState<string[]>([])

    useEffect(() => {
        if (!props.suggestionModel || !props.suggestionFieldName) {
            return
        }

        getDataFromSearchv4("", "companyTextFieldOptions", {
            model: props.suggestionModel,
            name: props.suggestionFieldName,
        })
            .then((response: Array<{ name: string }>) => {
                const names = response.map((item: { name: string }) => item.name).filter(Boolean)
                setAllSuggestions(names)
            })
            .catch(() => {
                // Silently fail — suggestions are optional
            })
    }, [props.suggestionModel, props.suggestionFieldName])

    const suggestionState = useSuggestions({
        allSuggestions,
        inputValue,
        excludeValues: values,
        onSelect: (value: string) => {
            setValues(prev => [...prev, value])
            setInputValue("")
            setTimeout(() => {
                inputRef.current?.focus()
            }, 0)
        },
        showAllOnEmpty: true,
    })

    const showTemporaryError = (message: string) => {
        if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current)
        }
        setError(message)
        errorTimeoutRef.current = setTimeout(() => {
            setError("")
            errorTimeoutRef.current = null
        }, WARNING_MESSAGE_DURATION)
    }

    useImperativeHandle(props.forwardedRef, () => ({
        getValue: () => {
            if (inputValue.trim()) {
                const finalValues = [...values]
                const trimmedInput = inputValue.trim()
                if (!values.includes(trimmedInput)) {
                    finalValues.push(trimmedInput)
                }
                return finalValues
            }
            return values
        },
        afterGuiAttached: () => {
            setTimeout(() => {
                inputRef.current?.focus()
            }, 0)
        },
    }))

    useEffect(() => {
        const timer = setTimeout(() => {
            inputRef.current?.focus()
        }, 10)

        return () => {
            clearTimeout(timer)
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current)
            }
        }
    }, [])

    const removeValue = (index: number) => {
        setValues(prev => prev.filter((_, i) => i !== index))
    }

    const addValue = (value: string) => {
        const trimmed = value.trim()
        if (!trimmed) return

        if (values.includes(trimmed)) {
            showTemporaryError(`"${trimmed}" is already in the list`)
            return
        }

        setValues(prev => [...prev, trimmed])
        setInputValue("")
    }

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault()
        const pastedText = e.clipboardData.getData("text")
        const pastedValues = pastedText
            .split(/[,;]/)
            .map(v => v.trim())
            .filter(v => v !== "")

        const newValues: string[] = []
        const duplicates: string[] = []

        pastedValues.forEach(value => {
            if (values.includes(value) || newValues.includes(value)) {
                duplicates.push(value)
            } else {
                newValues.push(value)
            }
        })

        if (newValues.length > 0) {
            setValues(prev => [...prev, ...newValues])
            setInputValue("")
        }

        if (duplicates.length > 0) {
            showTemporaryError(`Skipped duplicate(s): ${duplicates.join(", ")}`)
        }
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (suggestionState.handleSuggestionKeyDown(e)) return

        if (e.key === "," || e.key === ";") {
            e.preventDefault()
            if (inputValue.trim()) {
                addValue(inputValue)
            }
        } else if (e.key === "Enter") {
            e.preventDefault()
            if (inputValue.trim()) {
                addValue(inputValue)
            }
        } else if (e.key === "Backspace" && !inputValue && values.length > 0) {
            removeValue(values.length - 1)
        }
    }

    return (
        <TagEditorContainer>
            <TagsContainer>
                {values.map((value, index) => (
                    <Tag key={index}>
                        <TagText>{value}</TagText>
                        <RemoveButton
                            onClick={() => removeValue(index)}
                            type="button"
                            aria-label={`Remove ${value}`}
                        >
                            <IconX color={colorMiscBlue70} height={14} width={14} />
                        </RemoveButton>
                    </Tag>
                ))}
                <TagInput
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus={true}
                    onChange={e => {
                        setInputValue(e.target.value)
                        suggestionState.setSelectedSuggestionIndex(-1)
                    }}
                    onKeyDown={handleKeyDown}
                    onKeyDownCapture={suggestionState.handleSuggestionKeyDownCapture}
                    onPaste={handlePaste}
                    onBlur={e => {
                        if (suggestionState.isSuggestionBlur(e)) return
                        setTimeout(() => {
                            suggestionState.setShowSuggestions(false)
                            if (inputValue.trim()) {
                                addValue(inputValue)
                            }
                        }, 200)
                    }}
                    onFocus={suggestionState.handleSuggestionFocus}
                    placeholder={values.length === 0 ? "Type and press Enter..." : "Add more..."}
                />
            </TagsContainer>
            <SuggestionsDropdown {...suggestionState} />
            {error && <ErrorMessage>{error}</ErrorMessage>}
        </TagEditorContainer>
    )
}
